import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import { authAPI } from '../services/api';

const buttonSx = {
  mt: 3,
  mb: 2,
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
  '&:hover': {
    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)',
    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
    transform: 'translateY(-2px)',
  },
  transition: 'all 0.3s ease',
};

const STEP_EMAIL = 1;
const STEP_OTP = 2;
const STEP_NEW_PASSWORD = 3;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEP_EMAIL);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authAPI.forgotPassword(email);
      setStep(STEP_OTP);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authAPI.verifyResetOtp(email, otp);
      setStep(STEP_NEW_PASSWORD);
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired code.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      return setError('Password must be at least 8 characters.');
    }
    if (newPassword !== confirmPassword) {
      return setError('Passwords do not match.');
    }

    setLoading(true);
    try {
      await authAPI.resetPasswordWithOtp(email, otp, newPassword);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const stepConfig = {
    [STEP_EMAIL]: {
      title: 'Forgot Password',
      subtitle: "Enter your email and we'll send you a verification code",
    },
    [STEP_OTP]: {
      title: 'Enter Verification Code',
      subtitle: `We've sent a 6-digit code to ${email}`,
    },
    [STEP_NEW_PASSWORD]: {
      title: 'Set New Password',
      subtitle: 'Enter your new password below',
    },
  }[step];

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={4}
          sx={{
            p: 4,
            width: '100%',
            borderRadius: 3,
            background: 'linear-gradient(to bottom, rgba(37, 99, 235, 0.03) 0%, rgba(255,255,255,1) 30%)',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box
              component="img"
              src="/assets/logo.png"
              alt="ClassTree"
              sx={{ width: 72, height: 72, mb: 1.5, objectFit: 'contain' }}
            />
            <Typography component="h1" variant="h5" fontWeight="bold">
              {stepConfig.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
              {stepConfig.subtitle}
            </Typography>
          </Box>

          {success ? (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              Password reset! Redirecting to login...
            </Alert>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              {step === STEP_EMAIL && (
                <Box component="form" onSubmit={handleSendOtp} noValidate>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    disabled={loading}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading || !email}
                    sx={buttonSx}
                  >
                    {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Send Verification Code'}
                  </Button>
                </Box>
              )}

              {step === STEP_OTP && (
                <Box component="form" onSubmit={handleVerifyOtp} noValidate>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    label="6-Digit Code"
                    name="otp"
                    autoComplete="one-time-code"
                    autoFocus
                    inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                    value={otp}
                    onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '')); setError(''); }}
                    disabled={loading}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading || otp.length !== 6}
                    sx={buttonSx}
                  >
                    {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Verify Code'}
                  </Button>
                  <Button
                    fullWidth
                    variant="text"
                    disabled={loading}
                    onClick={() => { setStep(STEP_EMAIL); setOtp(''); setError(''); }}
                    sx={{ mb: 1 }}
                  >
                    Use a different email
                  </Button>
                </Box>
              )}

              {step === STEP_NEW_PASSWORD && (
                <Box component="form" onSubmit={handleResetPassword} noValidate>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    label="New Password"
                    name="newPassword"
                    type="password"
                    autoComplete="new-password"
                    autoFocus
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                    disabled={loading}
                    helperText="Minimum 8 characters"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    label="Confirm New Password"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                    disabled={loading}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading || !newPassword || !confirmPassword}
                    sx={buttonSx}
                  >
                    {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Reset Password'}
                  </Button>
                </Box>
              )}
            </>
          )}

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link component={RouterLink} to="/login" variant="body2" color="primary">
              Back to Sign In
            </Link>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPassword;
